import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Navigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import FlashOffIcon from '@mui/icons-material/FlashOff';
import FlashOnIcon from '@mui/icons-material/FlashOn';

import { QrScanner } from "react-qrcode-scanner";

import AuthService from '../services/auth';


function QRCode({setNavbarState, navbarState}) {
    const [redirect, setRedirect] = useState({ trigger: false, route: '/' });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [showBackdrop, setShowBackdrop] = useState(false);
    const [viewFocused, setViewFocused] = useState(true);
    const [flashEnabled, setFlashEnabled] = useState(false);
    const [hasNavigated, setHasNavigated] = useState(false);
    const scannerContainerRef = useRef(null);
    const cameraTrackRef = useRef(null);

    const publicUrl = process.env.REACT_APP_PUBLIC_URL;

    
    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
          return;
        }
        setSnackbar({ ...snackbar, open: false });
      };
    


    const stopCameraAndNavigate = (url) => {
        if (hasNavigated) return;
        setHasNavigated(true);
        if (cameraTrackRef.current) {
            cameraTrackRef.current.stop();
            cameraTrackRef.current = null;
        }
        setViewFocused(false);
        setTimeout(() => {
            window.location.href = url;
        }, 100);
    };

    const handleScan = (data) => {
        const baseUrl = `${publicUrl}/recepcion?codigo=`;
        if (data.startsWith(baseUrl)) {
            data = data.substring(baseUrl.length);
        } else if (data.length !== 6) {
            return;
        }
        stopCameraAndNavigate(`/recepcion?codigo=${data}&qr=1`);
    }

    const handleCancel = () => {
        stopCameraAndNavigate('/recepcion');
    }

    useEffect(() => {
        if (!AuthService.userRoleIncludes(["admin","receptor"])) {
            setRedirect({ trigger: true, route: '/inicio' });
        }
        setNavbarState({ ...navbarState, showBack: false, backTo: '/recepcion', showOptions: false, showSearch: false });
        
        const handleFocus = () => setViewFocused(true);
        const handleBlur = () => setViewFocused(false);

        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);

        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
            setViewFocused(false);
        };
    }, [])

    useEffect(() => {
        if (!viewFocused) return;
        let attempts = 0;
        const interval = setInterval(() => {
            const video = scannerContainerRef.current?.querySelector('video');
            const stream = video?.srcObject;
            const track = stream?.getVideoTracks?.()[0];

            if (track) {
                cameraTrackRef.current = track;
                clearInterval(interval);
            }

            attempts += 1;
            if (attempts > 10) {
                clearInterval(interval);
            }
        }, 300);

        return () => clearInterval(interval);
    }, [viewFocused]);

    useEffect(() => {
        const track = cameraTrackRef.current;
        if (!track || typeof track.getCapabilities !== 'function') return;

        const capabilities = track.getCapabilities();
        if (!capabilities?.torch) return;

        track
            .applyConstraints({ advanced: [{ torch: flashEnabled }] })
            .catch(() => {
                setSnackbar({
                    open: true,
                    message: 'El flash no esta disponible en este dispositivo.',
                    severity: 'warning',
                });
                setFlashEnabled(false);
            });
    }, [flashEnabled]);

    return (
        <Box sx={{ minHeight: '100dvh', bgcolor: 'black' }}>
            {redirect.trigger && (
                <Navigate to={redirect.route} replace={true} />
            )}
            <Snackbar open={snackbar.open} autoHideDuration={8000} anchorOrigin={{ vertical: "top", horizontal: "center" }} sx={{mt: "3.2rem"}} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
                {snackbar.message}
                </Alert>
            </Snackbar>
            <Backdrop open={showBackdrop} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Box
                sx={{
                    position: 'relative',
                    height: '100dvh',
                    width: '100%',
                    overflow: 'hidden',
                }}
                ref={scannerContainerRef}
            >
                {viewFocused && (
                    <QrScanner
                        onScan={(result) => handleScan(result)}
                        onError={(error) => console.log(error)}
                        showViewFinder={false}
                        resolution={600}
                        facingMode="environment"
                        legacyMode={false}
                        aspectRatio='9:16'
                        delay={500}
                        flipHorizontally={true}
                        reactivate={true}
                        reactivateTimeout={2000}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                        }}
                    />
                )}

                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: 0,
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.0) 30%, rgba(0,0,0,0.0) 70%, rgba(0,0,0,0.65) 100%)',
                        pointerEvents: 'none',
                    }}
                />

                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        px: 2.5,
                        pt: 4,
                        pb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        zIndex: 2,
                    }}
                >
                    <IconButton
                        onClick={handleCancel}
                        sx={{
                            bgcolor: 'rgba(0,0,0,0.45)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <IconButton
                        onClick={() => setFlashEnabled((prev) => !prev)}
                        sx={{
                            bgcolor: 'rgba(0,0,0,0.35)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' },
                        }}
                    >
                        {flashEnabled ? <FlashOnIcon /> : <FlashOffIcon />}
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '70px',
                        height: '70px',
                        border: '1px solid rgba(255,255,255,0.5)',
                        borderRadius: '12px',
                        opacity: 0.65,
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                />

                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 36,
                        left: 0,
                        right: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        zIndex: 2,
                        pointerEvents: 'none',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'rgba(255,255,255,0.85)',
                            bgcolor: 'rgba(0,0,0,0.35)',
                            px: 2,
                            py: 0.5,
                            borderRadius: '999px',
                            backdropFilter: 'blur(6px)',
                        }}
                    >
                        Enfoca el codigo QR dentro del recuadro
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default QRCode;

